import type React from 'react';

import classNames from 'classnames';
import { memo, useCallback, useMemo, useRef } from 'react';

import { Menu } from './menu';
import MenuItem from './menu-item';
import { useMenuNavigation } from './use-menu-navigation';
import { CheckIcon } from '../../icons/check';
import { ChevronIcon } from '../../icons/chevron';

import styles from './menu.module.css';

export type MenuOptions = {
  id: string;
  label?: React.ReactNode;
  value?: string | number;
  defaultValue?: string;
  showHeader?: boolean;
  options?: MenuOptions[] | undefined;
  headerSubmenu?: boolean;
  onClick?: React.MouseEventHandler<HTMLButtonElement> | undefined;
};

type MenuWrapperProps = {
  isOpen: boolean | undefined;
  onClose: () => void;
  menuOptions: MenuOptions;
  onChooseItem?: (currentScreen: string, itemId: string) => void;
  buttonName: string;
};

export function MenuWrapper({ isOpen, onClose, menuOptions, onChooseItem, buttonName }: MenuWrapperProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);

  const { currentScreen, setCurrentScreen, currentScreenData, parentScreen, onGoBack } = useMenuNavigation({
    menuOptions,
    isOpen: !!isOpen,
    onClose,
    buttonName,
    wrapperRef,
  });

  const headerSubmenu = useMemo(
    () => currentScreenData?.options?.find((option) => option.headerSubmenu),
    [currentScreenData?.options]
  );
  const menuItems = useMemo(
    () => currentScreenData?.options?.filter((option) => !option.headerSubmenu),
    [currentScreenData?.options]
  );

  const onHeaderSubmenuClick = useMemo(
    () =>
      headerSubmenu &&
      (headerSubmenu?.onClick ||
        (((e) => {
          e.stopPropagation();
          setCurrentScreen(headerSubmenu?.id);
        }) as React.MouseEventHandler<HTMLButtonElement>)),
    [headerSubmenu, setCurrentScreen]
  );

  return (
    <div ref={wrapperRef} className={classNames(styles.wrapper, !isOpen && styles.closed)} tabIndex={-1}>
      <Menu
        label={currentScreenData?.label}
        onGoBack={onGoBack}
        onClose={onClose}
        dataTestId={currentScreenData?.id}
        hasParent={!!parentScreen}
        showHeader={currentScreenData?.showHeader}
        headerSubmenu={headerSubmenu}
        onHeaderSubmenuClick={onHeaderSubmenuClick}
      >
        {menuItems?.map((option) => (
          <MenuWrapperItem
            key={`${currentScreenData?.id}-${option.id}`}
            option={option}
            value={currentScreenData?.value}
            onChooseItem={onChooseItem}
            onGoBack={onGoBack}
            currentScreen={currentScreen}
            setCurrentScreen={setCurrentScreen}
          />
        ))}
      </Menu>
    </div>
  );
}

const MenuWrapperItem = memo(function MenuWrapperItem({
  option,
  value,
  onChooseItem,
  onGoBack,
  currentScreen,
  setCurrentScreen,
}: {
  option: MenuOptions;
  value?: string | number;
  onChooseItem?: (currentScreen: string, itemId: string) => void;
  onGoBack: () => void;
  currentScreen: string;
  setCurrentScreen: (screenId: string) => void;
}) {
  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      if (option.onClick) {
        option.onClick(e);
      } else {
        if (!option.options?.length) {
          onChooseItem?.(currentScreen, option.id);
          onGoBack();
        } else {
          setCurrentScreen(option.id);
        }
      }
    },
    [currentScreen, onChooseItem, onGoBack, option, setCurrentScreen]
  );

  const currentValue = useMemo(
    () => option.options?.find((o) => o.id === option.value)?.label || option.defaultValue,
    [option.defaultValue, option.options, option.value]
  );

  return (
    <MenuItem
      data-testid={option.id || option.label}
      data-testtext={option.label}
      className={classNames({ [styles.active]: option.id === value })}
      onClick={handleClick}
      label={option.label}
      currentValue={currentValue}
      leftIcon={!option.options?.length ? option.id === value ? <CheckIcon /> : <></> : undefined}
      rightIcon={option.options?.length ? <ChevronIcon /> : undefined}
      data-goto={option.id}
      aria-current={option.id === value}
    />
  );
});
